import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';
import { Button } from '@empiriecom/module-components/Button';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config';
import { useWishList } from '@empiriecom/module-components/WishListProvider';
import { GtmEventProductInteraction } from '../Tracking/types';

const pulseAnimation = keyframes`
  0% { transform: scale(1) }
  50% { transform: scale(1.2) }
  100% { transform: scale(1) }
`;

const IconWrapper = styled.span<{ onWishlist: boolean; useMinified: boolean; fillColor: string }>`
  margin-left: ${({ useMinified }) => (useMinified ? '0' : '0.3rem')};
  /* Pulse Animation */
  ${({ onWishlist }) =>
    onWishlist &&
    css`
      animation: 1s ${pulseAnimation} ease-in-out;
    `}

  /* Hover - Color Animation */
  && .wishlist-css-32_svg__border {
    fill: ${({ onWishlist, theme, fillColor }) =>
      onWishlist ? theme[fillColor].color.normal : theme.greyLight.color.text};
  }
  && .wishlist-css-32_svg__fill {
    fill: ${({ onWishlist, theme, fillColor }) =>
      onWishlist ? theme[fillColor].color.normal : 'transparent'};
  }
  && * {
    transition: color 0.33s, stroke 0.33s, fill 0.33s;
    transition-timing-function: ease-in-out;
    will-change: color, stroke, transform;
  }

  /* Desktop only, because mobile touch keeps after click */
  @media (hover: hover) {
    &&:hover .wishlist-css-32_svg__border {
      fill: ${({ theme, onWishlist, fillColor }) =>
        onWishlist ? theme.greyLight.color.text : theme[fillColor].color.normal};
    }
  }
`;

const AddToWishlistButtonWrapper = styled.div<{ isIconOnly: boolean }>`
  .add-to-wishlist-button {
    padding: 0.4rem ${({ isIconOnly }) => !isIconOnly && '1rem'};
    height: 100%;
    ${({ isIconOnly }) => !isIconOnly && 'width:100%;'}
  }
`;

const HeartFilled = getIcon('WishlistFilled');

export interface AddToWishlistProps {
  /** The SKU of the added variation. */
  sku: string;
  /** The name of the added variation. */
  // name: string;
  /** Flag indicating if the button should render the icon and the label text or just the icon.
   *  @default true
   */
  showLabel?: boolean;
}

/**
 * This is the `AddToWishlist` button component to handle adding or removing products to or from the wishlist.
 * The button label as well as the icon will be updated depending on whether the product is on the wishlist or not.
 */
export const LegacyAddToWishlist: FunctionComponent<AddToWishlistProps> = ({
  sku,
  // name,
  showLabel = true,
}): JSX.Element => {
  const {
    addToWishlistButton: { iconFillColor },
  } = useConfig<FragmentConfiguration>();
  const { wishList, toggleSku } = useWishList();
  const dispatchGTMEvent = useTracking();

  return (
    <>
      {wishList && (
        <AddToWishlistButtonWrapper isIconOnly={!showLabel}>
          <Button
            className="add-to-wishlist-button"
            data-testid={`add-to-wishlist${showLabel ? '' : '-min'}`}
            layout="default"
            onClick={() => {
              toggleSku(sku);
              dispatchGTMEvent<GtmEventProductInteraction>({
                event: 'ga_event',
                eventAction: `wishlist${wishList.includes(sku) ? 'Remove' : 'Add'}`,
                eventCategory: 'Product Interactions',
                eventLabel: sku,
              });
            }}
            uppercase={false}
          >
            {showLabel ? (
              <span>
                {wishList.includes(sku) ? (
                  <>
                    <span>
                      <FormattedMessage
                        id="add-to-wishlist.label.remove"
                        defaultMessage="Vom Merkzettel entfernen"
                      />
                    </span>
                  </>
                ) : (
                  <>
                    <span>
                      <FormattedMessage
                        id="add-to-wishlist.label.add"
                        defaultMessage="Zum Merkzettel hinzufügen"
                      />
                    </span>
                  </>
                )}
                <IconWrapper
                  useMinified={false}
                  fillColor={iconFillColor}
                  onWishlist={wishList.includes(sku)}
                >
                  <HeartFilled
                    data-testid="heart-icon"
                    size="2rem"
                    color="inherit"
                    stroke="inherit"
                  />
                </IconWrapper>
              </span>
            ) : (
              <span>
                <IconWrapper
                  useMinified
                  fillColor={iconFillColor}
                  onWishlist={wishList.includes(sku)}
                >
                  <HeartFilled
                    data-testid="heart-icon"
                    size="2rem"
                    color="inherit"
                    stroke="inherit"
                  />
                </IconWrapper>
              </span>
            )}
          </Button>
        </AddToWishlistButtonWrapper>
      )}
    </>
  );
};

export default LegacyAddToWishlist;
